/**
 * Local-only provenance for one-way catalogue bootstrap imports.
 * Persisted on IndexedDB Strain records; never written to the server API.
 */

import type { Strain } from '@shared';

export const LOCAL_CATALOGUE_IMPORT_KIND =
  'imported_from_existing_catalogue' as const;

export interface LocalCatalogueImportProvenance {
  schemaVersion: 1;
  /** Distinguishes one-time bootstrap from ongoing spreadsheet sync. */
  kind: typeof LOCAL_CATALOGUE_IMPORT_KIND;
  /**
   * Server catalogue row identity. Used as the fallback for sources without a
   * spreadsheet Product ID; otherwise retained as supplemental provenance.
   */
  sourceServerStrainId: string;
  /**
   * Opaque, durable spreadsheet Product ID when the source had one. An exact
   * match in valid bootstrap provenance proves a prior import.
   */
  sourceProductId: string | null;
  importedAt: string;
}

export type StrainWithLocalImport = Strain & {
  localCatalogueImport?: LocalCatalogueImportProvenance | null;
};

export function isLocalCatalogueImportProvenance(
  value: unknown,
): value is LocalCatalogueImportProvenance {
  if (value == null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v.schemaVersion === 1 &&
    v.kind === LOCAL_CATALOGUE_IMPORT_KIND &&
    typeof v.sourceServerStrainId === 'string' &&
    v.sourceServerStrainId.length > 0 &&
    (v.sourceProductId === null || typeof v.sourceProductId === 'string') &&
    typeof v.importedAt === 'string'
  );
}

export function getLocalCatalogueImport(
  strain: Strain | StrainWithLocalImport,
): LocalCatalogueImportProvenance | null {
  const raw = (strain as StrainWithLocalImport).localCatalogueImport;
  return isLocalCatalogueImportProvenance(raw) ? raw : null;
}

export function createLocalCatalogueImportProvenance(source: {
  id: string;
  productId: string | null;
  importedAt?: string;
}): LocalCatalogueImportProvenance {
  return {
    schemaVersion: 1,
    kind: LOCAL_CATALOGUE_IMPORT_KIND,
    sourceServerStrainId: source.id,
    sourceProductId: source.productId,
    importedAt: source.importedAt ?? new Date().toISOString(),
  };
}
